import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import FieldWrapper from '../field-wrapper/field-wrapper.vue';
import AbstractField from '@/app/modules/shared/components/forms/inputs/abstract-field/abstract-field';
import {
  GeoPostalCodeAutocompleteHelper,
  GeoPostalCodeSuggestion,
  useGeoPostalCodeAutocompleteHelper,
} from '@core/helpers/geo-postal-code-autocomplete.helper';

import '@trevoreyre/autocomplete-vue/dist/style.css';
import Autocomplete from '@trevoreyre/autocomplete-vue';
import { FilterObject } from '@shared/interfaces/config';

@Component({
  name: 'bc-postal-code-input',
  components: {
    'bpc-field-wrapper': FieldWrapper,
    Autocomplete,
  },
})
export default class PostalCodeInput extends mixins(AbstractField) {
  @Prop([String, Number]) readonly value: string | number;
  @Prop({ type: Boolean, default: false }) readonly autofocus: boolean;
  @Prop({ type: Array }) readonly countries: string[];
  @Prop({ type: Array }) readonly filters: FilterObject[];

  geoHelper: GeoPostalCodeAutocompleteHelper = useGeoPostalCodeAutocompleteHelper();

  mounted() {
    // The component doesn't support autofocus natively so we apply it manually if it's enabled
    this.$nextTick(() => {
      if (this.autofocus) {
        const autocompleteComponent = this.$refs.autoCompleteRef as Vue;

        if (autocompleteComponent && autocompleteComponent.$refs) {
          const inputElement = autocompleteComponent.$refs.input as HTMLInputElement;
          if (inputElement) {
            // Use requestAnimationFrame to be sure that the input is rendered before trying to focus it
            requestAnimationFrame(() => {
              inputElement.focus();
            });
          }
        }
      }

      // Add a global listener for keydown events on the component
      if (this.$el) {
        this.$el.addEventListener('keydown', this.handleComponentKeyDown);
      }

      // Initialize the value of the component if there is an initial value
      if (this.value) {
        this.updateAutocompleteValue(this.value);
      }
    });
  }

  beforeDestroy() {
    // Clean the listener when the component is destroyed
    if (this.$el) {
      this.$el.removeEventListener('keydown', this.handleComponentKeyDown);
    }
  }

  handleComponentKeyDown(event) {
    // This function will be called when a key is pressed
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.$emit('esc-key');
    } else if (event.key === 'Enter') {
      this.$emit('enter-key');
    }
  }

  get model(): string | number {
    return this.value;
  }
  set model(val: string | number) {
    this.$emit('input', val);
    if (this.eventsConfig && this.eventsConfig.emit) {
      this.eventsConfig.emit.forEach(event => {
        this.$root.$emit(event, val);
      });
    }
  }

  private externalFormModelCountryFilter?: string =
    (this.filters?.find(filter => {
      return filter['filterField'] === 'country';
    })?.value as string) || null;

  get countriesForFiltering() {
    if (this.countries && this.countries.length > 0) {
      return this.countries;
    } else if (this.externalFormModelCountryFilter) {
      return [this.externalFormModelCountryFilter];
    } else {
      return [this.$store.getters['centerSettings/countryCode']];
    }
  }

  async search(input: string) {
    this.model = input;
    if (input.length < 1) {
      return [];
    }

    try {
      return await this.geoHelper.getCitiesByPostalCode(input, this.countriesForFiltering);
    } catch (error) {
      return [];
    }
  }

  getResultValue(result: GeoPostalCodeSuggestion): string {
    return result.postalCode;
  }

  async handleSubmit(result: GeoPostalCodeSuggestion): Promise<void> {
    this.model = result.postalCode;
    // Global event for standard form
    this.$emit('input-custom-event', {
      eventName: 'selectedCityFromPostalCode',
      value: result.city,
    });

    this.$emit('input-custom-event', {
      eventName: 'selectedCountryFromPostalCode',
      value: result.countryCode,
    });

    // Local event for parent component
    this.$emit('selected-city', result.city);
    this.$emit('selected-country', result.countryCode);
  }

  extendHandleEvent(eventName: string, event: any, val: any) {
    if (eventName === 'setCountryFilterFromCountrySelector') {
      this.externalFormModelCountryFilter = val ? val : null;
    }
  }

  // Add watch to detect changes in the value prop
  @Watch('value')
  onValueChange(newValue: string | number) {
    this.updateAutocompleteValue(newValue);
  }

  // Method to update the value of the autocomplete component to simulate v-model
  updateAutocompleteValue(newValue: string | number) {
    this.$nextTick(() => {
      const autocompleteComponent = this.$refs.autoCompleteRef as any;
      if (autocompleteComponent) {
        // Update the value of the autocomplete component directly
        autocompleteComponent.value = String(newValue || '');
      }
    });
  }
}
